import { SerializedAttachment } from "../types/interfaces/chats";

export const getFileSize = (
  size: number,
  tensPower: number = 0,
  fractionDigits: number = 1
) => {
  // tensPower=6 - ожидаем на вход мегабайты
  // tensPower=3 - ожидаем на вход килобайты
  // Возводим в степень число 10 и умножаем на переданное число, получаем байты.
  // Чтобы принимать на вход байты, килобайты, мегабайты и т.д
  let _bytes = size * 10 ** tensPower;

  const _GB = _bytes / 10 ** 9;
  if (_GB > 1) {
    // Десятичных знаков
    return `${_GB.toFixed(fractionDigits)} GB`;
  }
  const _MB = _bytes / 10 ** 6;
  if (_MB > 0.01) {
    // Десятичных знаков
    return `${_MB.toFixed(fractionDigits)} MB`;
  }
  const _KB = _bytes / 10 ** 3;
  if (_KB > 1) {
    // Десятичных знаков
    return `${_KB.toFixed(fractionDigits)} KB`;
  }
  return `${_bytes} B`;
};

export const getFileName = (name: string, max: number = 16) => {
  // Название файла с расширением указанной длины. Если больше, то сокращается всё до расширения.
  if (name.length <= max) {
    return name;
  }
  const name_parts = name.split(".");
  const extension = name_parts.length > 1 ? name_parts.pop() : null;
  const useful_name_length = extension ? max - extension.length - 3 : max;
  const useful_name = name.slice(0, useful_name_length);
  return `${useful_name}...${extension}`;
};

export const getExtension = (name: string) => {
  // сюда приходит и имя файла и url
  // у подписанной ссылки бывают query и hash без их отсечения расширение определялось неверно
  const clean = name.split("?")[0].split("#")[0];
  const name_parts = clean.split("/").pop()?.split(".") ?? [];

  return name_parts.length > 1 ? (name_parts.pop() as string) : null;
};

const hasExtension = (name: string, extensions: string[]) => {
  const extension = getExtension(name);

  return !!(extension && extensions.includes(extension.toLowerCase()));
};

export const isImage = (name: string) =>
  hasExtension(name, ["jpg", "jpeg", "png", "gif"]);

export const isVideo = (name: string) => hasExtension(name, ["avi", "mp4"]);

export const isPdf = (name: string) => hasExtension(name, ["pdf"]);

export const isDocx = (name: string) => hasExtension(name, ["docx"]);

export const isXls = (name: string) => hasExtension(name, ["xls", "xlsx"]);

export const getMediaTypeName = (name: string) => {
  const extension = getExtension(name);
  const documentExtensions = ["docx", "pdf", "xls", "xlsx"];
  const imageExtensions = ["jpg", "jpeg", "png", "gif"];

  if (extension) {
    if (documentExtensions.includes(extension.toLowerCase())) {
      return "Документ";
    }
    if (imageExtensions.includes(extension.toLowerCase())) {
      return "Фотография";
    }
  }
  return "Файл";
};

export const arrayBufferToBase64 = (buffer: ArrayBuffer): string => {
  const binary = new Uint8Array(buffer);
  let binaryString = "";
  for (let i = 0; i < binary.byteLength; i++) {
    binaryString += String.fromCharCode(binary[i]);
  }
  return btoa(binaryString);
};

const base64ToArrayBuffer = (base64: string): ArrayBuffer => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer;
};

export const serializeFile = (file: File) => {
  return new Promise<SerializedAttachment>((resolve, reject) => {
    const reader = new FileReader();
    if (file.size > 500 * 1024) {
      // Больше 500кб не сохраняем в черновик
      reject(`Файлы больше 500Kb не сохранятся в черновике, ${file.name}`);
    }
    reader.onload = (e) => {
      const arrayBuffer = e.target?.result as ArrayBuffer;
      const base64String = arrayBufferToBase64(arrayBuffer);
      resolve({
        name: file.name,
        content: base64String,
        type: file.type,
        size: file.size,
        lastModified: file.lastModified,
      });
    };
    reader.onerror = (error) => reject(error);
    reader.readAsArrayBuffer(file);
  });
};

export const deserializeFile = (fileData: SerializedAttachment): File => {
  return new File(
    [new Blob([base64ToArrayBuffer(fileData.content)])],
    fileData.name,
    { type: fileData.type, lastModified: fileData.lastModified }
  );
};

export const deserializeFiles = (fileData: SerializedAttachment[]): File[] => {
  return fileData.map((data) => deserializeFile(data));
};
